import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Bell, Check, CheckCircle, Trash, EnvelopeSimple, TelegramLogo, WhatsappLogo } from '@phosphor-icons/react';

export default function NotificationsPage() {
  const { api } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchNotifications = async () => {
    try {
      const res = await api.get('/notifications');
      setNotifications(res.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  useEffect(() => { fetchNotifications(); }, [api]);

  const markRead = async (id) => {
    await api.put(`/notifications/${id}/read`);
    fetchNotifications();
  };

  const markAllRead = async () => {
    await api.put('/notifications/read-all');
    fetchNotifications();
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  if (loading) return <div className="flex justify-center py-20"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div data-testid="notifications-page" className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <p className="overline">Notification Center</p>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Alerts</h1>
          {unreadCount > 0 && <p className="text-sm text-muted-foreground mt-1">{unreadCount} unread</p>}
        </div>
        {unreadCount > 0 && (
          <button onClick={markAllRead} data-testid="mark-all-read-btn" className="px-4 py-2 rounded-full border border-border text-sm font-medium hover:bg-muted flex items-center gap-2 self-start">
            <CheckCircle size={16} /> Mark all read
          </button>
        )}
      </div>

      <div className="space-y-2">
        {notifications.map(n => (
          <div key={n.notification_id} data-testid={`notification-${n.notification_id}`}
            className={`rounded-xl border bg-card p-4 flex items-start gap-4 transition-colors
              ${n.read ? 'border-border opacity-60' : 'border-primary/20 bg-primary/5'}`}>
            <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0
              ${n.priority === 'high' ? 'bg-red-500/10 text-red-500' : n.priority === 'medium' ? 'bg-yellow-500/10 text-yellow-600' : 'bg-muted text-muted-foreground'}`}>
              <Bell size={20} weight="duotone" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-medium text-sm">{n.title}</p>
                  <p className="text-sm text-muted-foreground mt-0.5">{n.message}</p>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  {!n.read && (
                    <button onClick={() => markRead(n.notification_id)} className="p-1.5 rounded-lg hover:bg-muted" title="Mark read">
                      <Check size={14} />
                    </button>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3 mt-2">
                <span className="text-xs text-muted-foreground">{new Date(n.created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium
                  ${n.priority === 'high' ? 'bg-red-500/10 text-red-500' : n.priority === 'medium' ? 'bg-yellow-500/10 text-yellow-600' : 'bg-muted text-muted-foreground'}`}>
                  {n.priority}
                </span>
              </div>
            </div>
          </div>
        ))}
        {notifications.length === 0 && (
          <div className="text-center py-20">
            <Bell size={48} className="mx-auto text-muted-foreground/30 mb-4" />
            <p className="text-muted-foreground">No notifications yet</p>
            <p className="text-sm text-muted-foreground mt-1">Alerts from staff, expenses, and events will appear here</p>
          </div>
        )}
      </div>
    </div>
  );
}
